import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    NgZone,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { buildProviders, Coerce, KalDialogConfig, KalDialogService } from '@kalidea/kaligraphi';
import { AppFile } from 'src/app/utils/file.utils';
import { environment } from 'src/environments/environment';
import {
    AbstractControl,
    FormGroup,
    FormArray,
    FormBuilder,
    FormControl,
    Validators
} from '@angular/forms';
import { EMAIL_REGEXP, FormValidator } from 'src/app/validators/form-validator';
import { UserState } from 'src/app/_user/store/user/user.state';
import { Store } from '@ngxs/store';
import {
    ArrayOfModelEmailModelEmailVuePubliModel,
    CommissionMailModel,
    FichierCompletModel,
    ModelEmailModelEmailVuePubliModel,
    MutationGetIdTypePublipostageArgs,
    MutationGetListeCommissionMailArgs,
    MutationGetListeModelEmailPubliArgs,
    MutationGetListePubliDocumentReportArgs,
    MutationGetModelEmailPubliArgs,
    PubliDocumentReportModel
} from 'src/graphql/generated-types';
import { catchError, distinctUntilChanged, filter, map, startWith, switchMap, take, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { Observable, of, Subscription, throwError } from 'rxjs';
import gql from 'graphql-tag';
import { arrayEmpty, arrayNotEmpty, arrayOnlyOne } from 'src/app/utils/array.utils';
import {
    DocumentBankListDialogComponent,
    DocumentBankListDialogData
} from 'src/app/_shared/dialogs/document-bank-dialog/document-bank-list-dialog/document-bank-list-dialog.component';
import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';
import { isNullOrEmpty } from 'src/app/utils/string.utils';
import uniqBy from 'lodash-es/uniqBy';
import { SharedEditorUploadImageButton } from 'src/app/_shared/classes/tinymce/shared-editor-upload-image-button';
import { SharedEditorGedSelectionButton } from 'src/app/_shared/classes/tinymce/shared-editor-ged-selection-button';

export interface SharedMail {
    isNewsLetter?: boolean;

    publipostageTypeId?: number;

    model?: string;

    to?: 'personal' | 'professional' | 'both' | 'privileged';

    from?: 'ce' | 'commission' | 'user' | 'freeEntry';

    privilegedAddress?: 'personal';

    fromMail?: string;

    subject?: string;

    content?: string;

    footer?: string;

    vouchersList?: FichierCompletModel[];

    reportsDocumentsList?: ReportDocument[];

    generatedDocumentsList?: GeneratedDocumentSelection[];

    themeIdList?: number[];
}

type ReportDocument = { selected: boolean, id: number, label: string };

type GeneratedDocumentSelection = { selected: boolean, label: string };

export interface GeneratedMailDocument {
    label: string;

    selected?: boolean;

    generate$: () => Observable<FichierCompletModel>;
}

export type SharedMailPublipostageCode = 'BENEF' | 'DEVIS' | 'FAC';

export type DocumentCodesListMode = 'mandatory' | 'preselected' | 'skip';

const MAX_LENGTH_SUBJECT = 100;

export const MailContentPlaceholder = {
    BODY_NEWSLETTER: '##BODYNEWSLETTER##',
    IMG_NEWSLETTER: '##IMGNEWSLETTER##',
    FOOTER_NEWSLETTER: '##FOOTERNEWSLETTER##'
} as const;

@Component({
    selector: 'app-shared-mail',
    templateUrl: './shared-mail.component.html',
    styleUrls: ['./shared-mail.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedMailComponent),
    standalone: false
})
export class SharedMailComponent extends CustomFormControlAccess implements OnInit {

    form: FormGroup;

    commissionMailList: CommissionMailModel[] = [];

    modelEmailList: ModelEmailModelEmailVuePubliModel[] = [];

    shouldDisplayTagsList = false;

    mandatoryDocumentsList: PubliDocumentReportModel[] = [];

    allowedExtensions: string[] = environment.extensions.all;

    readonly editorConfig = {
        ...environment.tinymce.config,
        plugins: 'fullscreen lists link' + (this.hasModuleEditAvanceTB ? ' table' : ''),
        toolbar: 'imageupload fontfamily | bold italic underline alignleft aligncenter alignright alignjustify link gedselection fontsize forecolor bullist | fullscreen'
            + (this.hasModuleEditAvanceTB ? ' | table tabledelete | tableprops tablerowprops tablecellprops | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol' : ''),
        link_context_toolbar: false,
        height: 300,
        setup: editor => {
            editor.on('init', () => {
                editor.execCommand('fontName', false, 'Verdana');
                // execCommand fait prendre le focus à editor, cela fait scroller le parent vers editor
                editor.iframeElement.blur();
            });

            if (this.hasModuleEditAvance) {
                new SharedEditorUploadImageButton(editor, this.store, this.graphqlService);
                new SharedEditorGedSelectionButton(editor, this.ngZone, this.dialogService);
            }
        }
    };

    @Input() publipostageCode: SharedMailPublipostageCode = null;

    @Input() defaultCodeModel: number | string | null = null;

    @Input() documentCodesList = [];

    @Input() documentCodesListMode: DocumentCodesListMode = 'mandatory';

    @Input() viewId: number | null = null;

    @Input() generatedDocumentsList: GeneratedMailDocument[] = [];

    @Input()
    @Coerce('boolean')
    hasFooter = false;

    private notifyUpdateSubscription: Subscription = null;

    private readonly ngZone: NgZone;

    constructor(protected readonly fb: FormBuilder,
                protected readonly store: Store,
                protected readonly graphqlService: GraphqlService,
                protected readonly cdr: ChangeDetectorRef,
                protected readonly dialogService: KalDialogService) {
        super();
        this.ngZone = inject(NgZone);
    }

    private _loading = false;

    get loading(): boolean {
        return this._loading;
    }

    set loading(loading: boolean) {
        this._loading = loading;
        this.cdr.markForCheck();
    }

    @Input()
    set editorHeight(height: number) {
        this.editorConfig.height = height;
    }

    @Input()
    @Coerce('boolean')
    set editorResizable(isResizable: boolean) {
        this.editorConfig.resize = isResizable;
        // La poignée de redimensionnement n'est incluse que dans la barre d'état.
        // Il faut donc forcer son affichage si on veut redimensionner verticalement l'éditeur
        // Sauf cette exception, la barre d'état n'est pas utilisée dans UpExpert.
        this.editorConfig.statusbar = isResizable;
    }

    get ceMail(): string {
        const {useNoReply, noReplyAddress} = this.store.selectSnapshot(UserState.noReplyMailCE);
        return useNoReply ? noReplyAddress : this.store.selectSnapshot(UserState.currentDelegation).CEEmail;
    }

    get userMailsAddressesList(): string[] {
        const connectedUser = this.store.selectSnapshot(UserState.connectedUser);
        return [connectedUser.MailPerso, connectedUser.MailPro].filter(e => !!e);
    }

    get vouchersList(): FormArray {
        return this.form.get('vouchersList') as FormArray;
    }

    get reportsDocuments(): FormArray {
        return this.form.get('reportsDocumentsList') as FormArray;
    }

    get generatedDocuments(): FormArray {
        return this.form.get('generatedDocumentsList') as FormArray;
    }

    get fromValue(): string {
        return this.form.value.from;
    }

    private get hasModuleEditAvanceTB(): boolean {
        return this.store.selectSnapshot(UserState.hasModules('EDITAVANCETB'));
    }

    private get hasModuleEditAvance(): boolean {
        return this.store.selectSnapshot(UserState.hasModules('EDITAVANCE'));
    }

    private get publipostageTypeId(): number | null {
        return this.form.value.publipostageTypeId || null;
    }

    static IsValid(mail: SharedMail): boolean {
        const isRecipientValid = (mail?.to === 'privileged')
            ? !isNullOrEmpty(mail?.privilegedAddress)
            : !isNullOrEmpty(mail?.to);

        return mail
            && isRecipientValid
            && ['from', 'subject', 'content'].every(e => !isNullOrEmpty(mail[e]))
            && !!mail.fromMail
            && (mail.from === 'ce' || EMAIL_REGEXP.test(mail.fromMail))
            && mail?.subject?.length <= MAX_LENGTH_SUBJECT;
    }

    static Validator(control: AbstractControl): { invalidsharedmail: boolean } | null {
        const mail: SharedMail = control.value;
        return SharedMailComponent.IsValid(mail) ? null : {invalidsharedmail: true};
    }

    writeValue(value: SharedMail): void {
        // on stoppe la subscription le temps de mettre à jour le formulaire.
        this.notifyUpdateSubscription.unsubscribe();

        this.vouchersList.clear();
        this.reportsDocuments.clear();

        if (!value) {
            if(this.form.dirty) {
                this.form.reset();
            }
        } else {
            this.updateForm(value);
            value.vouchersList?.forEach(v => this.vouchersList.push(new FormControl<FichierCompletModel>(v)));
            value.reportsDocumentsList?.forEach(v => this.reportsDocuments.push(new FormControl<ReportDocument>(v)));
            super.writeValue(value);
        }

        this.setFromMailValidator(value?.from || 'ce');

        this.initFormSubscription();

        this.cdr.markForCheck();
    }

    deleteVoucher(index: number): void {
        this.vouchersList.removeAt(index);
        this.cdr.markForCheck();
    }

    openDocumentsBankDialog(isLocalFileUpload = false): void {
        const config = new KalDialogConfig<DocumentBankListDialogData>({
            title: 'Ajouter un document',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            data: {
                shouldDisableSaveDocument: isLocalFileUpload,
                shouldToggleLocalFileUpload: isLocalFileUpload
            }
        });
        const dialogRef = this.dialogService.open(DocumentBankListDialogComponent, config);

        this.watch$ = dialogRef.afterClosed.pipe(
            filter(result => !!result?.selectedDocument),
            tap(({selectedDocument}) => {
                if (!selectedDocument.IdFile || this.vouchersList.controls.every(({value}) => value.IdFile !== selectedDocument.IdFile)) {
                    this.vouchersList.push(new FormControl<FichierCompletModel>({
                        ...selectedDocument,
                        FileBytes: isLocalFileUpload ? selectedDocument.FileBytes : null,
                        Filename: isLocalFileUpload ? selectedDocument.Filename.slice(0, -(selectedDocument.Extension.length + 1)) : selectedDocument.Filename
                    }));
                    this.cdr.markForCheck();
                }
            })
        );
    }

    addLocalVoucher($event: AppFile): void {
        if (!$event) {
            return;
        }

        this.vouchersList.push(new FormControl<FichierCompletModel>({
            IdLienFichier: 0,
            IsCommentable: false,
            IsLikeable: false,
            IdFile: null,
            Taille: 0,
            Extension: $event.extension,
            FileBytes: $event.fileBlob?.split(',')?.[1] || null,
            Filename: $event.file.name,
            LibelleLien: $event.file.name,
            StatutCode: 'VAL',
            DateDebut: null,
            DateFin: null,
            EditCategCode: null,
            TypeFichierIcone: null,
            TypeFichierLibelle: null,
            RemarqueLien: null,
            Url: null
        }));

        this.cdr.markForCheck();
    }

    toggleDisplayedTagsList(): void {
        this.shouldDisplayTagsList = !this.shouldDisplayTagsList;
    }

    updateFormValueAndValidity(): void {
        this.form.updateValueAndValidity();
    }

    createReportDocument(documentsList: PubliDocumentReportModel[]): void {
        this.notifyUpdateSubscription?.unsubscribe();
        this.reportsDocuments.clear();

        const isMandatoryMode = this.documentCodesListMode === 'mandatory';

        documentsList.forEach(document => {
            const isPreselected = this.documentCodesList
                ?.some(code => code === document.CodeDocument);

            this.reportsDocuments.push(this.fb.group({
                id: [document.IdReport || null],
                label: [document.LibelleDocumentContexte || null],
                selected: [{value: isPreselected, disabled: isPreselected && isMandatoryMode}]
            }));
        });

        this.notifyUpdate(this.form.getRawValue());
        this.initFormSubscription();
    }

    protected getReportsDocumentsList(publipostageTypeId: number, activityIdsList?: number[]): Observable<PubliDocumentReportModel[]> {
        if (this.documentCodesListMode === 'skip') {
            return of([]);
        }

        return this.graphqlService.mutateOne<'GetListePubliDocumentReport', MutationGetListePubliDocumentReportArgs>({
            mutation: gql`
                mutation GetListePubliDocumentReport($contexte: ContextInfosInput, $criteres: CriteresPubliDocumentInput) {
                    GetListePubliDocumentReport(
                        contexte: $contexte
                        criteres: $criteres
                    ) {
                        PubliDocumentReportModel {
                            IdReport
                            IdDocumentContexte
                            LibelleDocumentContexte
                            CodeDocument
                        }
                    }
                }`,
            variables: {
                criteres: {
                    IdTypePubli: publipostageTypeId,
                    ListeIdTypeActivite: activityIdsList ? {
                        decimal: activityIdsList
                    } : null
                }
            },
        }).pipe(
            map(GetListePubliDocumentReport => {
                return GetListePubliDocumentReport?.PubliDocumentReportModel || [];
            })
        );
    }

    private updateForm(mail: SharedMail): void {
        this.form.patchValue({
            publipostageTypeId: mail.publipostageTypeId || null,
            model: mail.model || null,
            to: mail.to || 'personal',
            from: mail.from || 'ce',
            privilegedAddress: mail.privilegedAddress || 'personal',
            fromMail: mail.from ? (mail.fromMail || '') : this.ceMail,
            subject: mail.subject || '',
            content: mail.content || '',
            footer: mail.footer || ''
        });
    }

    private initFormSubscription(): void {
        // restoration de la subscription
        this.notifyUpdateSubscription = this.form.valueChanges.pipe(
            this.untilDestroyed,
            tap(() => this.notifyUpdate(this.form.getRawValue()))
        ).subscribe();
    }

    private getCommissionMailList$(): Observable<CommissionMailModel[]> {
        return this.graphqlService.mutateOne<'GetListeCommissionMail', MutationGetListeCommissionMailArgs>({
            mutation: gql`
                mutation GetListeCommissionMail($contexte: ContextInfosInput, $listeIdCom: ArrayOfdecimalInput) {
                    GetListeCommissionMail(contexte:$contexte, listeIdCom: $listeIdCom) {
                        CommissionMailModel {
                            AdresseMail
                            LibelleCommission
                        }
                    }
                }
            `,
            variables: {
                listeIdCom: {decimal: this.store.selectSnapshot(UserState.commissionsRightsList).map(c => c.IdCommission)}
            }
        }).pipe(
            map(GetListeCommissionMail => {
                this.commissionMailList = GetListeCommissionMail?.CommissionMailModel || [];
                return this.commissionMailList;
            }),
        );
    }

    private getPublipostageTypeId$(): Observable<number> {

        return this.graphqlService.mutateOne<'GetIdTypePublipostage', MutationGetIdTypePublipostageArgs>({
            mutation: gql`
                mutation GetIdTypePublipostage($contexte: ContextInfosInput, $codeType: String) {
                    GetIdTypePublipostage(contexte: $contexte, codeType: $codeType)
                }`,
            variables: {
                codeType: this.publipostageCode
            }
        }).pipe(
            map(GetIdTypePublipostage => {
                this.form.get('publipostageTypeId').patchValue(GetIdTypePublipostage);
                return GetIdTypePublipostage;
            }),
        );
    }

    private getModelsList$(): Observable<ArrayOfModelEmailModelEmailVuePubliModel> {
        return this.graphqlService.mutateOne<'GetListeModelEmailPubli', MutationGetListeModelEmailPubliArgs>({
            mutation: gql`
                mutation GetListeModelEmailPubli($contexte: ContextInfosInput, $crit: ModelEmailCriteresModelEmailModelInput) {
                    GetListeModelEmailPubli(
                        contexte: $contexte,
                        crit: $crit
                    ) {
                        ModelEmailModelEmailVuePubliModel {
                            IdModelEmail
                            CodeModele
                            LibelleModelEmail
                            IdVuePubli
                        }
                    }
                }`,
            variables: {
                crit: {
                    IdTypePubli: this.publipostageTypeId,
                    IsClos: false,
                    IsModelPublipostage: true
                }
            }
        }).pipe(
            tap(GetListeModelEmailPubli => {
                this.modelEmailList = GetListeModelEmailPubli?.ModelEmailModelEmailVuePubliModel || [];
            }),
        );
    }

    private getModel$(idMail: number): Observable<ModelEmailModelEmailVuePubliModel> {
        if (!idMail) {
            return of(null);
        }
        return this.graphqlService.mutateOne<'GetModelEmailPubli', MutationGetModelEmailPubliArgs>({
            mutation: gql`
                mutation GetModelEmailPubli($contexte: ContextInfosInput, $idMail: Float) {
                    GetModelEmailPubli(
                        contexte: $contexte,
                        idMail: $idMail
                    ) {
                        IdModelEmail
                        ObjectModelEmail
                        CorpsModelEmail
                        LibelleModelEmail
                        CodeModele
                        ListIdTypeActivite {
                            decimal
                        }
                        ListeIdReportJoint {
                            decimal
                        }
                        ListePJ {
                            FichierModel {
                                IdFile
                                Filename
                                Extension
                                LibelleLien
                                Token
                                Url
                            }
                        }
                    }
                }`,
            variables: {
                idMail: +idMail
            }
        }).pipe(
            map(GetModelEmailPubli => GetModelEmailPubli || null),
        );
    }

    private setFromMailValidator(value: string): void {
        const fromMail = this.form.get('fromMail');
        fromMail.clearValidators();

        switch (value) {
            case 'commission':
                fromMail.setValidators(FormValidator.commissionHasEmail);
                fromMail.markAsDirty();
                break;
            case 'user':
                fromMail.setValidators(FormValidator.isNotNull);
                fromMail.markAsDirty();
                break;
            case 'freeEntry':
                fromMail.setValidators([FormValidator.isNotNull, FormValidator.email]);
                break;
        }

        fromMail.updateValueAndValidity({emitEvent: false});
    }

    private handleReportsOnModelChanged$(model: ModelEmailModelEmailVuePubliModel): Observable<ModelEmailModelEmailVuePubliModel> {
        const isMandatoryMode = this.documentCodesListMode === 'mandatory';

        // Si aucun modèle d'e-mail n'est sélectionné, on affiche les reports obligatoires qui ont été transmis
        if (arrayNotEmpty(this.documentCodesList) && !model && isMandatoryMode) {
            this.createReportDocument(this.mandatoryDocumentsList);
            return of(model);
        }

        // En mode preselected sans modèle sélectionné : on garde les FormGroups créés à l'init,
        // sinon un 2ᵉ createReportDocument détache les kal-checkbox de leur FormControl (binding orphelin).
        if (!isMandatoryMode && !model) {
            return of(model);
        }

        const activityIdsList = model?.ListIdTypeActivite?.decimal;
        // Lorsque l'on change de modèle de-mail, on récupère la liste des reports correspondants au paramétrage du modèle
        return this.getReportsDocumentsList(this.publipostageTypeId, activityIdsList).pipe(
            // Si des reports obligatoires été transmis et qu'ils ne sont pas présents dans les reports récupérés
            // On les ajoute à la liste (mode mandatory uniquement)
            map(documentsList => isMandatoryMode
                ? uniqBy([...this.mandatoryDocumentsList, ...documentsList], (item) => item.CodeDocument)
                : documentsList
            ),
            tap(documentsList => this.createReportDocument(documentsList)),
            map(() => model)
        );
    }

    private initSubscription(): void {
        const model = this.form.get('model');

        this.watch$ = model.valueChanges.pipe(
            startWith(model.value),
            filter(() => !!this.publipostageCode),
            distinctUntilChanged(),
            tap(() => this.loading = true),
            switchMap(modelId => this.getModel$(modelId)),
            tap((selectedModel) => {
                const content = selectedModel?.CorpsModelEmail || null;
                const subject = selectedModel?.ObjectModelEmail || null;
                const documents = selectedModel?.ListePJ?.FichierModel || [];
                // On retire les pj correspondant au model précédent
                this.vouchersList.clear();

                // Ajout des pj du model sélectionné
                documents.forEach(document => this.vouchersList.push(new FormControl(document)));

                this.form.patchValue({
                    content,
                    subject,
                });
            }),
            // Gestion de la liste des reports proposée à la sélection
            switchMap(selectedModel => this.handleReportsOnModelChanged$(selectedModel)),
            // On sélectionne tous les reports qui se trouvent dans le model sélectionné
            tap(selectedModel => {
                (selectedModel?.ListeIdReportJoint?.decimal || []).forEach(e => {
                    const reportIndex = this.reportsDocuments.controls
                        .findIndex(({value}) => `${value.id}` === `${e}`);

                    if (reportIndex > -1) {
                        this.reportsDocuments.controls[reportIndex]
                            .patchValue({selected: true});
                    }
                });
            }),
            tap(() => {
                this.loading = false;
                this.form.updateValueAndValidity({emitEvent: false});
            }),
            catchError(err => {
                this.loading = false;
                return throwError(() => err);
            })
        );

        this.watch$ = this.form.get('from').valueChanges.pipe(
            startWith(this.form.value.from),
            tap(value => {
                const fromMail = this.form.get('fromMail');

                switch (value) {
                    case 'commission':
                        fromMail.patchValue(null);
                        break;
                    case 'user':
                        fromMail.patchValue(null);
                        break;
                    case 'freeEntry':
                        fromMail.reset();
                        break;
                    case 'ce':
                        fromMail.patchValue(this.ceMail);
                        break;
                }

                this.setFromMailValidator(value);
            })
        );

        this.watch$ = this.form.get('to').valueChanges.pipe(
            tap(to => {
                const privilegedAddressControl = this.form.get('privilegedAddress');
                privilegedAddressControl.clearValidators();

                let privilegedAddress = '';
                if (to === 'privileged') {
                    privilegedAddressControl.setValidators([FormValidator.isNotNull]);
                    privilegedAddress = privilegedAddressControl.value || 'personal';
                }

                privilegedAddressControl.patchValue(privilegedAddress, {emitEvent: false});
            })
        );
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            publipostageTypeId: [null],
            model: [null],
            reportsDocumentsList: this.fb.array([]),
            generatedDocumentsList: this.fb.array(
                (this.generatedDocumentsList || []).map(({label, selected}) => this.fb.group({
                    label: [label],
                    selected: [!!selected]
                }))
            ),
            to: ['personal', [FormValidator.isNotNull]],
            from: ['ce', [FormValidator.isNotNull]],
            privilegedAddress: ['personal'],
            fromMail: [''],
            subject: ['', [FormValidator.isNotNull, Validators.maxLength(MAX_LENGTH_SUBJECT)]],
            content: ['', [FormValidator.isNotNull]],
            footer: [''],
            vouchersList: this.fb.array([])
        });

        this.initFormSubscription();

        this.loading = true;

        this.watch$ = this.getCommissionMailList$().pipe(
            take(1),
            tap(() => this.initSubscription()),
            tap(() => this.loading = false),
            filter(() => !!this.publipostageCode),
            tap(() => this.loading = true),
            switchMap(() => this.getPublipostageTypeId$()),
            switchMap(() => {
                return this.getModelsList$().pipe(
                    tap(res => {
                        if (this.defaultCodeModel) {
                            this.modelEmailList = res?.ModelEmailModelEmailVuePubliModel?.filter(e =>
                                [e.CodeModele, e.IdModelEmail].includes(+this.defaultCodeModel || this.defaultCodeModel));
                        }
                        if(arrayNotEmpty(this.modelEmailList)){
                            this.modelEmailList = this.modelEmailList.sort((a, b) => {
                                const libA = a.LibelleModelEmail.toUpperCase();
                                const libB = b.LibelleModelEmail.toUpperCase();
                                return libA.localeCompare(libB);
                            });
                        }
                    })
                );
            }),
            switchMap(() => {
                return this.getReportsDocumentsList(this.publipostageTypeId).pipe(
                    tap((documentsList: PubliDocumentReportModel[]) => {
                        const isMandatoryMode = this.documentCodesListMode === 'mandatory';
                        // En mode 'mandatory', on filtre la liste aux codes obligatoires.
                        // En mode 'preselected', on garde toute la liste (la pré-sélection ne masque pas les autres options).
                        const displayedDocumentsList = (arrayEmpty(this.documentCodesList) || !isMandatoryMode)
                            ? documentsList
                            : documentsList.filter(({CodeDocument}) => this.documentCodesList.includes(CodeDocument));

                        if (arrayNotEmpty(this.documentCodesList) && isMandatoryMode) {
                            this.mandatoryDocumentsList = displayedDocumentsList;
                        }

                        this.createReportDocument(displayedDocumentsList);
                    })
                );
            }),
            tap(() => this.loading = false),
            filter(() => !!this.defaultCodeModel || !!this.viewId),
            tap(() => {
                let modelId = null;

                if (!!this.defaultCodeModel) {
                    const modelEmailId = this.modelEmailList.find(m => m.CodeModele === this.defaultCodeModel)?.IdModelEmail;
                    modelId = modelEmailId || (arrayOnlyOne(this.modelEmailList) ? this.modelEmailList[0].IdModelEmail : null);
                } else if (!!this.viewId) {
                    const modelEmailId = this.modelEmailList.find(m => m.IdVuePubli === this.viewId)?.IdModelEmail;
                    modelId = modelEmailId || (arrayOnlyOne(this.modelEmailList) ? this.modelEmailList[0].IdModelEmail : null);
                }

                this.form.get('model').patchValue(modelId);
            })
        );
    }
}
